import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, AtSign, FileText, Camera, CheckCircle2, XCircle, Loader2, Zap } from 'lucide-react';
import { db, auth, storage } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';

export const OnboardingPage = () => {
  const navigate = useNavigate();

  // Data State
  const [uid, setUid] = useState(null);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [bio, setBio] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  
  // Logic State
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Refs
  const searchTimeout = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkUser = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      
      try {
        setUid(user.uid);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          const redirect = new URLSearchParams(window.location.search).get('redirect');
          
          if (data.isSetupComplete) {
            navigate(redirect || '/');
            return;
          }
          if (!data.college) {
             navigate('/college-selection');
             return;
          }
          
          setName(data.name || user.displayName || '');
          setUsername(data.username || '');
          setOriginalUsername(data.username || '');
          setBio(data.bio || '');
          setPhotoURL(data.photoURL || user.photoURL || '');
          if (data.username) setIsUsernameAvailable(true);
        } else {
          setName(user.displayName || '');
          setPhotoURL(user.photoURL || '');
        }
      } catch (error) {
        // console.error(error);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, [navigate]);

  const handleUsernameChange = (e) => {
    const text = e.target.value;
    const formatted = text.toLowerCase().replace(/[^a-z0-9_.]/g, '');
    setUsername(formatted);
    setIsUsernameAvailable(null);
    setErrorMsg('');
    
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (formatted.length > 2) {
      setCheckingUsername(true);
      searchTimeout.current = setTimeout(async () => {
        if (formatted === originalUsername) {
          setIsUsernameAvailable(true);
          setCheckingUsername(false);
          return;
        }
        try {
          const docSnap = await getDoc(doc(db, 'usernames', formatted));
          setIsUsernameAvailable(!docSnap.exists());
        } catch (e) {
          // console.error("Check error", e);
        } finally {
          setCheckingUsername(false);
        }
      }, 500);
    } else {
      setCheckingUsername(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please select a valid image file.');
      return;
    }

    try {
      setUploading(true);
      const storageRef = ref(storage, `profile_pictures/${uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      setPhotoURL(url);
      await setDoc(doc(db, 'users', uid), { photoURL: url }, { merge: true });
    } catch (error) {
      // console.error(error);
      setErrorMsg('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) return setErrorMsg('Please enter your name.');
    if (!username.trim() || username.length < 3) return setErrorMsg('Username must be at least 3 characters.');
    if (isUsernameAvailable === false) return setErrorMsg('That username is already taken.');
    
    setSaving(true);
    setErrorMsg('');

    try {
      if (username !== originalUsername) {
        // Double check username availability transactionally
        const check = await getDoc(doc(db, 'usernames', username));
        if (check.exists()) {
          setIsUsernameAvailable(false);
          setSaving(false);
          return setErrorMsg('Someone just took that username.');
        }
        await setDoc(doc(db, 'usernames', username), { uid: uid });
        // Clean up old username if exists (skipping transaction for brevity on frontend MVP)
      }

      await setDoc(doc(db, 'users', uid), {
        name,
        username,
        bio,
        photoURL,
        isSetupComplete: true,
      }, { merge: true });

      const redirect = new URLSearchParams(window.location.search).get('redirect');
      navigate(redirect || '/');

    } catch (error) {
      // console.error(error);
      setErrorMsg('Could not save profile.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <Loader2 className="w-10 h-10 text-[#0BD9E5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black flex flex-col items-center justify-center font-poppins text-white px-4 py-12">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(11,217,229,0.1),transparent_70%)]" />
      </div>

      <div className="relative z-10 w-full max-w-md bg-[#0f1214] border border-[#0BD9E5]/20 rounded-3xl p-8 shadow-[0_0_40px_rgba(11,217,229,0.1)]">
        
        <div className="text-center mb-8">
          <h2 className="text-3xl font-black mb-2 tracking-tight">Create Profile</h2>
          <p className="text-gray-400 text-sm">Let's get you set up. This is how the world will see you.</p>
        </div>

        {/* Avatar Upload */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            <div className={`w-28 h-28 rounded-full border-2 ${photoURL ? 'border-[#0BD9E5]' : 'border-gray-700'} bg-[#1a1a1a] overflow-hidden flex items-center justify-center relative group`}>
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                  <Loader2 className="w-8 h-8 text-[#0BD9E5] animate-spin" />
                </div>
              )}
              {photoURL ? (
                <img src={photoURL} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User size={40} className="text-gray-600" />
              )}
              
              <div 
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity z-20"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="text-white w-8 h-8" />
              </div>
            </div>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 bg-[#0BD9E5] p-2 rounded-full text-black hover:bg-white transition-colors z-30 shadow-lg"
            >
              <Camera size={16} strokeWidth={2.5} />
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImageUpload} 
              accept="image/*" 
              className="hidden" 
            />
          </div>
          <div className="flex items-center gap-3 mt-4">
            <span className="text-[#0BD9E5] text-sm font-medium cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              {photoURL ? 'Change Picture' : 'Add Picture'}
            </span>
            {photoURL && (
              <button 
                type="button" 
                onClick={() => setPhotoURL('')} 
                className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
              >
                Remove Picture
              </button>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Name */}
          <div className="flex flex-col gap-2">
            <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Name</label>
            <div className="relative flex items-center bg-[#1a1a1a] border border-gray-800 rounded-xl px-4 py-3 focus-within:border-[#0BD9E5] transition-colors">
              <User size={18} className="text-[#0BD9E5] mr-3" />
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                maxLength={20}
                placeholder="Your Name" 
                className="bg-transparent text-white w-full focus:outline-none"
              />
              <span className="text-gray-600 text-xs">{name.length}/20</span>
            </div>
          </div>

          {/* Username */}
          <div className="flex flex-col gap-2">
            <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Username</label>
            <div className={`relative flex items-center bg-[#1a1a1a] border rounded-xl px-4 py-3 transition-colors ${
              isUsernameAvailable === false ? 'border-red-500' : 
              isUsernameAvailable === true ? 'border-green-500' : 'border-gray-800 focus-within:border-[#0BD9E5]'
            }`}>
              <AtSign size={18} className="text-[#0BD9E5] mr-3" />
              <input 
                type="text" 
                value={username} 
                onChange={handleUsernameChange}
                maxLength={20}
                placeholder="unique_handle" 
                className="bg-transparent text-white w-full focus:outline-none lowercase"
              />
              <div className="ml-2 flex items-center justify-center w-5">
                {checkingUsername ? <Loader2 size={16} className="text-[#0BD9E5] animate-spin" /> : 
                 isUsernameAvailable === true ? <CheckCircle2 size={16} className="text-green-500" /> :
                 isUsernameAvailable === false ? <XCircle size={16} className="text-red-500" /> : null}
              </div>
            </div>
            {isUsernameAvailable === false && <span className="text-red-500 text-xs mt-1">This username is taken.</span>}
          </div>

          {/* Bio */}
          <div className="flex flex-col gap-2">
            <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Bio</label>
            <div className="relative flex items-start bg-[#1a1a1a] border border-gray-800 rounded-xl px-4 py-3 focus-within:border-[#0BD9E5] transition-colors">
              <FileText size={18} className="text-[#0BD9E5] mr-3 mt-1 shrink-0" />
              <textarea 
                value={bio} 
                onChange={(e) => setBio(e.target.value)}
                maxLength={150}
                placeholder="Tell the world about your spark..." 
                className="bg-transparent text-white w-full focus:outline-none resize-none h-20"
              />
              <span className={`absolute bottom-2 right-3 text-xs ${bio.length >= 150 ? 'text-red-500' : 'text-gray-600'}`}>
                {bio.length}/150
              </span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {errorMsg && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: 'auto' }} 
              exit={{ opacity: 0, height: 0 }}
              className="text-red-500 text-sm text-center mt-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
            >
              {errorMsg}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Button */}
        <button 
          onClick={handleSaveProfile}
          disabled={saving || checkingUsername || uploading}
          className="w-full mt-8 bg-white hover:bg-gray-200 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 relative overflow-hidden group"
        >
          {saving ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              Launch Profile
              <Zap size={18} className="text-black group-hover:scale-110 transition-transform" fill="currentColor" />
            </>
          )}
        </button>

      </div>
    </div>
  );
};

export default OnboardingPage;

